import { LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { Box, Stack, useTheme } from "@mui/material";
import { useScrollOffset } from "@src/appV2/lib/utils/useScrollOffset";
import { groupBy } from "lodash";
import { forwardRef, type RefObject, useMemo, useRef } from "react";
import {
  GroupedVirtuoso,
  type GroupedVirtuosoHandle,
  type GroupedVirtuosoProps,
  type GroupProps,
} from "react-virtuoso";

interface StickyGroupHeaderProps extends GroupProps {
  scrollContainerElement: HTMLElement | undefined;
  backgroundColor?: string;
}

function StickyGroupHeader(props: StickyGroupHeaderProps) {
  const { scrollContainerElement, backgroundColor, ...rest } = props;
  const headerRef = useRef<HTMLDivElement>(null);
  const { isStuck } = useScrollOffset(scrollContainerElement, headerRef);

  return (
    <Box
      ref={headerRef}
      sx={(theme) => ({
        top: 0,
        paddingY: theme.spacing(5),
        backgroundColor,
        boxShadow: `inset 0 -1px 0 0 ${isStuck ? theme.palette.border?.primary : "transparent"}`,
        borderRadius: 0,
        transition: theme.transitions.create("box-shadow"),
      })}
      {...rest}
    />
  );
}

function Footer(props: {
  isLoadingMore?: boolean;
  loadingText?: string;
  customContent?: React.ReactNode;
}) {
  const { isLoadingMore, loadingText, customContent } = props;

  return (
    <Stack px={5} minHeight="76px">
      {isLoadingMore && (
        <Stack alignItems="center" py={8}>
          <LoadingSpinner size="medium" variant="primary" />
          {loadingText && (
            <Text mt={4} pb={10}>
              {loadingText}
            </Text>
          )}
        </Stack>
      )}
      {customContent}
    </Stack>
  );
}

interface GroupedListProps<T> extends GroupedVirtuosoProps<T, unknown> {
  items: T[];
  groupBy: (item: T) => string;
  renderGroupHeader: (groupKey: string, itemCount: number) => React.ReactNode;
  renderItem: (item: T) => React.ReactNode;
  backgroundColor?: string;
  isLoadingMore?: boolean;
  loadingText?: string;
  customScrollParentRef: RefObject<HTMLDivElement>;
  contentPlaceholder?: React.ReactNode;
  showPlaceholder?: boolean;
  scrollerTestId?: string;
  renderListHeader?: () => React.ReactNode;
  renderListFooter?: () => React.ReactNode;
  sortGroupKeys?: (keyA: string, keyB: string) => number;
}

function BaseGroupedList<T>(props: GroupedListProps<T>, ref: React.Ref<GroupedVirtuosoHandle>) {
  const theme = useTheme();
  const {
    items,
    groupBy: groupByFunction,
    renderGroupHeader,
    renderItem,
    backgroundColor = theme.palette.background.default,
    isLoadingMore,
    loadingText,
    customScrollParentRef,
    scrollerTestId,
    contentPlaceholder,
    showPlaceholder,
    renderListHeader,
    renderListFooter,
    sortGroupKeys,
    ...restProps
  } = props;

  const group = useMemo(() => {
    const itemsByGroup = groupBy(items, groupByFunction);
    const groupKeys = Object.keys(itemsByGroup).sort(sortGroupKeys);
    const counts = groupKeys.map((key) => itemsByGroup[key]?.length ?? 0);
    return { counts, groupKeys, itemsByGroup };
  }, [items, groupByFunction, sortGroupKeys]);

  // Memoize the components object to prevent re-renders
  const virtuosoComponents = useMemo(() => {
    function Group(groupProps: GroupProps) {
      return (
        <StickyGroupHeader
          {...groupProps}
          backgroundColor={backgroundColor}
          scrollContainerElement={customScrollParentRef.current ?? undefined}
        />
      );
    }

    return {
      Group,
      Footer: () => (
        <Footer
          isLoadingMore={isLoadingMore}
          loadingText={loadingText}
          customContent={renderListFooter?.()}
        />
      ),
    };
  }, [backgroundColor, customScrollParentRef, isLoadingMore, loadingText, renderListFooter]);

  return (
    <Box
      ref={customScrollParentRef}
      sx={{
        flex: 1,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
      data-testid={scrollerTestId}
    >
      {renderListHeader?.()}
      {showPlaceholder ? (
        contentPlaceholder
      ) : (
        <GroupedVirtuoso
          ref={ref}
          customScrollParent={customScrollParentRef.current ?? undefined}
          groupCounts={group.counts}
          groupContent={(index) => {
            const groupKey = group.groupKeys[index];
            if (!groupKey) {
              return null;
            }

            const itemCount = group.counts[index];
            return renderGroupHeader(groupKey, itemCount);
          }}
          itemContent={(index, groupIndex) => {
            const item = items[index];
            const isFirstInGroup =
              index === group.counts.slice(0, groupIndex).reduce((sum, count) => sum + count, 0);

            if (!item) {
              return null;
            }

            return <Box pt={isFirstInGroup ? 0 : 5}>{renderItem(item)}</Box>;
          }}
          components={virtuosoComponents}
          {...restProps}
        />
      )}
    </Box>
  );
}

type GroupedListComponent = <T>(
  props: GroupedListProps<T> & { ref?: React.Ref<GroupedVirtuosoHandle> }
) => JSX.Element;

export const GroupedList = forwardRef(BaseGroupedList) as GroupedListComponent;
